import { zodResolver } from '@hookform/resolvers/zod';
import { QuoteAcceptanceModeEnum, QuoteStatusEnum } from '@monetize/types/app';
import {
  nullifyEmptyStrings,
  parseDateAsLocalTime,
  toDateOnly,
  toNoonUtcTimestamp,
} from '@monetize/utils/core';
import {
  FunctionComponent as FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDropzone } from 'react-dropzone';
import { Controller, useForm } from 'react-hook-form';
import { MdInsertDriveFile } from 'react-icons/md';
import * as z from 'zod';
import { handleApiErrorToast } from '../../../api/axios';
import {
  MAlert,
  MBox,
  MButton,
  MCenterModal,
  MFlex,
  MFormField,
  MGrid,
  MGridItem,
  MIcon,
  MLink,
  MStack,
  MText,
  MTextarea,
} from '../../../components/Monetize';
import { DatePicker } from '../../../components/Monetize/DatePicker/DatePicker';
import { MFileDragDropUpload } from '../../../components/Monetize/MFileDragDropUpload';
import { useQuote, useQuoteSettings } from '../../../hooks';

export const ManualAcceptanceSchema = z.object({
  signatureDate: z
    .string()
    .nonempty('Please provide the date this quote was signed.'),
  manualAcceptanceReason: z
    .string()
    .nonempty('Please provide a reason to manually accept this quote.'),
});

type IManualAcceptanceSchema = z.infer<typeof ManualAcceptanceSchema>;

interface ManualQuoteAcceptanceModalProps {
  isOpen: boolean;
  quoteId: string;
  onClose: () => void;
}

/**
 * The signature date is submitted as noon UTC and the server's "today" is the UTC day, so the field both defaults
 * to and is capped at the UTC day. Using the local day instead would offer dates the accept endpoint rejects.
 */
const todayInUtc = () => toDateOnly(new Date(), 'UTC');

const buildFormDefault = (): IManualAcceptanceSchema => ({
  signatureDate: todayInUtc(),
  manualAcceptanceReason: '',
});

export const ManualQuoteAcceptanceModal: FC<ManualQuoteAcceptanceModalProps> = (
  props: ManualQuoteAcceptanceModalProps,
) => {
  const { isOpen, onClose, quoteId } = props;
  const [signedQuote, setSignedQuote] = useState<File | null>(null);
  const { transitionQuoteStatus } = useQuote();
  const { quoteSettings } = useQuoteSettings();

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty, isValid, isSubmitting },
    reset,
  } = useForm<IManualAcceptanceSchema>({
    resolver: zodResolver(ManualAcceptanceSchema),
    mode: 'onChange',
    defaultValues: buildFormDefault(),
  });

  const latestSignatureDate = todayInUtc();
  const maxSignatureDate = useMemo(
    () => parseDateAsLocalTime(latestSignatureDate),
    [latestSignatureDate],
  );

  useEffect(() => {
    reset(buildFormDefault());
    setSignedQuote(null);
  }, [reset, isOpen]);

  const onSubmit = async (requestData: IManualAcceptanceSchema) => {
    const data = new FormData();
    data.append(
      'metadata',
      new Blob(
        [
          JSON.stringify(
            nullifyEmptyStrings({
              acceptanceReason: requestData.manualAcceptanceReason,
              acceptedAt: toNoonUtcTimestamp(requestData.signatureDate),
              mode: quoteSettings?.pauseAtAccepted
                ? QuoteAcceptanceModeEnum.PAUSE_AT_ACCEPTED
                : QuoteAcceptanceModeEnum.PROCESS_AUTOMATICALLY,
            }),
          ),
        ],
        {
          type: 'application/json',
        },
      ),
    );

    if (signedQuote) {
      data.append('signedQuote', signedQuote);
    }

    try {
      await transitionQuoteStatus(quoteId, {
        newState: QuoteStatusEnum.ACCEPTED,
        extraData: data,
      });
      onClose();
    } catch (err) {
      handleApiErrorToast(err);
    }
  };

  const onDropAccepted = useCallback((acceptedFiles: File[]) => {
    setSignedQuote(acceptedFiles[0]);
  }, []);

  const { open } = useDropzone({
    accept: { 'application/pdf': [] },
    multiple: false,
    onDropAccepted,
  });

  return (
    <MCenterModal
      isOpen={isOpen}
      onClose={onClose}
      closeOnOverlayClick={false}
      closeOnEsc={false}
      modalTitle={`Reason for Manually Accepting`}
      size="md"
      modalHeaderProps={{
        paddingBottom: 0,
      }}
      renderFooter={() => (
        <MStack gap={4} direction="row" align="center" justify="right" flex={1}>
          <MButton onClick={onClose} variant="cancel" minW="auto">
            Cancel
          </MButton>
          <MButton
            variant="primary"
            isLoading={isSubmitting}
            isDisabled={!isDirty || !isValid || isSubmitting}
            data-testid="manually-accept-modal-btn"
            minW="auto"
            type="submit"
            form="quote-acceptance-form"
          >
            Accept
          </MButton>
        </MStack>
      )}
    >
      <MBox borderRadius={3}>
        <form id="quote-acceptance-form" onSubmit={handleSubmit(onSubmit)}>
          <MGrid templateColumns="repeat(12, 1fr)" gap={1}>
            <MGridItem colSpan={12}>
              <MText color="tGray.darkPurple" mb="2">
                To manually accept this quote, please confirm when it was signed
                and provide a reason. You can also upload a signed quote.
              </MText>
              <MFormField
                error={errors?.signatureDate}
                label="Signature Date"
                isRequired
                mb="3"
              >
                <Controller
                  name="signatureDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      maxDate={maxSignatureDate}
                      isDisabled={isSubmitting}
                      data-testid="manual-acceptance-signature-date"
                    />
                  )}
                />
              </MFormField>
              <MFormField
                error={errors?.manualAcceptanceReason}
                label="Reason"
                isRequired
              >
                <Controller
                  name="manualAcceptanceReason"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <MTextarea
                      {...field}
                      data-testid="manual-acceptance-reason"
                      isDisabled={isSubmitting}
                    />
                  )}
                />
              </MFormField>
            </MGridItem>
          </MGrid>
        </form>
      </MBox>
      {signedQuote ? (
        <MBox mt={1}>
          <MFlex align="center" p={1}>
            <MIcon as={MdInsertDriveFile} w={6} h={6} />
            <MText ml={1}>{signedQuote.name}</MText>
            <MLink ml={2} textDecor="underline" fontSize="sm" onClick={open}>
              Change
            </MLink>
          </MFlex>
          <MAlert
            type="info"
            mt={2}
            mb={2}
            borderRadius={4}
            bgColor="tBlue.hover"
          >
            <MText>You cannot change this document once it's uploaded.</MText>
          </MAlert>
        </MBox>
      ) : (
        <MFileDragDropUpload
          p={5}
          mt={4}
          accept={['pdf']}
          label="Drag and Drop your quote file here"
          onFileUpload={setSignedQuote}
          maxFileSize={10}
        />
      )}
    </MCenterModal>
  );
};
